/**
 * Lane SCENE-PLAINS: the dressing of the outer places of the 256 m plains (bandit stockade, bridge, ruined temple,
 * graves, shadow camp, blood arena, wood clearings, bear ridge). Pure positions (map metres) in the shape of
 * `PlacedProp`; `plains.ts` appends them to `PLAINS_PROPS`, so the same list is drawn, lit and laid solid
 * (plainsMap.ts) with the rest. Every piece keeps `road half-width + 1` clear of every road and trail.
 * Arena banners stand on the far (screen-up) half of the ring only; the ring is R 10, props on R 7.5-9.
 */
import { hash2 } from '../../rng';
import type { PlacedProp, PlainsPropId, RoadDef, XY } from './plains';

function segDist(px: number, py: number, a: XY, b: XY): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const t = Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(px - (a[0] + dx * t), py - (a[1] + dy * t));
}

export function buildOuter(roads: readonly RoadDef[], bridge: XY): PlacedProp[] {
  const out: PlacedProp[] = [];
  const nearRoad = (x: number, y: number) =>
    roads.some((r) => {
      for (let k = 0; k + 1 < r.pts.length; k++) if (segDist(x, y, r.pts[k]!, r.pts[k + 1]!) < r.w / 2 + 1) return true;
      return false;
    });
  const put = (id: PlainsPropId, x: number, y: number, solid = false, onRoad = false) => {
    if (!onRoad && nearRoad(x, y)) return;
    out.push({ id, at: [x, y], solid });
  };

  // ── Bandit hill (15): palisade ring round the stockade (x 38-62, y 201-219) at margin 1.5; north gap x 48-52. ──
  {
    const x0 = 36.5;
    const x1 = 63.5;
    const y0 = 199.5;
    const y1 = 220.5;
    for (let x = x0 + 1; x < x1; x += 2) {
      if (!(x + 1 > 48 && x - 1 < 52)) put('palisadeX', x, y0, true);
      put('palisadeX', x, y1, true);
    }
    for (let y = y0 + 1; y < y1; y += 2) {
      put('palisadeY', x0, y, true);
      put('palisadeY', x1, y, true);
    }
    put('campfire', 50.5, 196.5);
    put('campfire', 66.5, 212.5);
    put('bannerBlack', 47.2, 198.4, true);
    put('bannerBlack', 52.8, 198.4, true);
    put('logPile', 33.5, 206.5, true);
    put('logPile', 66.5, 205.5, true);
  }

  // ── The bridge (150,126): stone rails along both edges, lantern poles at the four corners. ──
  for (let x = bridge[0] - 3.5; x <= bridge[0] + 4.6; x += 2) {
    put('stoneRailX', x, 121.8, false, true);
    put('stoneRailX', x, 126.2, false, true);
  }
  for (const [dx, dy] of [[-5, 121.8], [5, 121.8], [-5, 126.4], [5, 126.4]] as const) put('lanternPole2', bridge[0] + dx, dy, false, true);

  // ── Ruined temple (16): cracked bell and altar on the court, lions at the door, stupas in the ruin. ──
  put('lionL', 26.5, 117.5, true);
  put('lionR', 33.5, 117.5, true);
  put('brokenBell', 30.5, 121.5, true);
  put('altar', 30.5, 124.5, true);
  for (const [x, y] of [[18.5, 122.5], [42.5, 122.5], [19.5, 134.5], [43.5, 134.5], [31.5, 135.5]] as const) put('stupa', x, y, true);
  for (const [x, y] of [[14.5, 112.5], [46.5, 110.5], [40.5, 100.5]] as const) put('rocks', x, y);

  // ── Graves (18): slabs on a loose grid (i%4==2, j%3==1), about half kept; none on the shrine yard. ──
  for (let j = 197; j < 232; j++) {
    for (let i = 170; i < 212; i++) {
      if (i % 4 !== 2 || j % 3 !== 1) continue;
      if (i >= 191 && i <= 201 && j >= 199 && j <= 208) continue;
      const h = hash2(i, j);
      if (h > 0.5) continue;
      put(h < 0.1 ? 'tombstoneTall' : 'tombstone', i + 0.5, j + 0.5, true);
    }
  }
  for (const [x, y] of [[192.5, 209.5], [200.5, 209.5]] as const) put('torchPost', x, y, true);

  // ── Shadow camp (20): bamboo fence ring round the camp (x 24-44, y 18-34) at margin 1.5; gap on the SE. ──
  {
    const x0 = 22.5;
    const x1 = 45.5;
    const y0 = 16.5;
    const y1 = 35.5;
    const gap = (x: number, y: number) => Math.hypot(x - 45.5, y - 33) < 3.5;
    for (let x = x0 + 1; x < x1; x += 2) {
      put('bambooFenceX', x, y0, true);
      if (!gap(x, y1)) put('bambooFenceX', x, y1, true);
    }
    for (let y = y0 + 1; y < y1; y += 2) {
      put('bambooFenceY', x0, y, true);
      if (!gap(x1, y)) put('bambooFenceY', x1, y, true);
    }
    put('campfire', 49.5, 38.5);
  }

  // ── Blood arena (19): drums low all round, banners on the far half only (R 7.5-9 of the ring R 10 at 230,232). ──
  {
    const cx = 230.5;
    const cy = 232.5;
    for (const deg of [20, 75, 135, 200, 330]) {
      const a = (deg * Math.PI) / 180;
      put('warDrum', cx + Math.cos(a) * 8.5, cy + Math.sin(a) * 8.5, true);
    }
    for (const [deg, id] of [[160, 'bannerRed'], [250, 'bannerBlack'], [285, 'bannerRed'], [310, 'bannerBlack']] as const) {
      const a = (deg * Math.PI) / 180;
      put(id, cx + Math.cos(a) * 8.2, cy + Math.sin(a) * 8.2, true);
    }
  }

  // ── Wood (14) clearings and the bear ridge (17): logs and rocks. ──
  for (const [x, y] of [[205.5, 30.5], [214.5, 52.5], [226.5, 36.5]] as const) put('logPile', x, y, true);
  for (const [x, y] of [[208.5, 33.5], [217.5, 50.5], [224.5, 40.5], [232.5, 56.5]] as const) put('rocks', x, y);
  for (const [x, y] of [[236.5, 104.5], [240.5, 130.5], [228.5, 142.5], [242.5, 96.5]] as const) put('rocks2', x, y);
  return out;
}
